import { useState } from "react";
import { Link } from "react-router-dom";
import { useMoneyAccounts } from "@/api/cash";
import {
  usePayoutsDue,
  useRecordExpense,
  type PayoutDueRow,
  type PayoutStatus,
  type PayoutTotal,
} from "@/api/finance";
import { ExpenseForm } from "@/components/forms/ExpenseForm";
import { RequestError } from "@/components/RequestError";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { EmptyState } from "@/components/ui/empty-state";
import { SelectField } from "@/components/ui/field";
import { formatInr, formatMoney } from "@/lib/format";
import { formatTotals } from "@/lib/money";

const TH = "px-4 py-2 font-medium";
const THEAD =
  "border-b bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground";
const TD = "whitespace-nowrap px-4 py-2 text-right tabular-nums";

const STATUS: Record<
  PayoutStatus,
  { label: string; tone: "destructive" | "warning" | "success" | "muted" }
> = {
  overpaid: { label: "Overpaid", tone: "destructive" },
  ahead: { label: "Paid ahead of the client", tone: "warning" },
  due: { label: "Due", tone: "warning" },
  partial: { label: "Part paid", tone: "warning" },
  settled: { label: "Settled", tone: "success" },
  waiting: { label: "Waiting for the client", tone: "muted" },
};

/** What the Pay form starts from: what is pending now, at the rate the invoice achieved. */
export function payPrefill(row: PayoutDueRow): { amount: string; rate: string } {
  return {
    amount: Number(row.pending) > 0 ? row.pending : "",
    rate: row.rate_inr ?? "",
  };
}

/** "≈ INR 6,680.00", plus the part that has no rupee figure yet. */
export function rupeesOwed(total: PayoutTotal): string {
  const valued = Number(total.due_inr) > 0 ? `≈ ${formatInr(total.due_inr)}` : "";
  const unvalued = total.not_valued.length
    ? `${formatTotals(total.not_valued)} not valued yet`
    : "";
  return [valued, unvalued].filter(Boolean).join(" + ") || "—";
}

function payeeOf(row: { payee: string }): string {
  return row.payee || "No payee named";
}

export function PayoutsPanel() {
  const [includeSettled, setIncludeSettled] = useState(false);
  const [bucketId, setBucketId] = useState("");
  const [paying, setPaying] = useState<PayoutDueRow | null>(null);
  const report = usePayoutsDue(includeSettled);
  const accounts = useMoneyAccounts();
  const record = useRecordExpense();

  if (report.isPending) return <p className="text-muted-foreground">Loading payouts…</p>;
  if (report.isError) return <RequestError error={report.error} />;
  const data = report.data;
  const owed = data.totals.filter((t) => t.due.length > 0);
  const rows = data.rows.filter((row) => !bucketId || row.bucket_id === bucketId);
  const bucketOptions = data.funds.map((f) => ({ value: f.bucket_id, label: f.name }));

  return (
    <div className="flex flex-col gap-6">
      {paying && (
        <Card className="border-primary/40">
          <CardHeader>
            <CardTitle>
              Pay {paying.bucket_name}
              {paying.payee ? ` (${paying.payee})` : ""} - {paying.invoice_no}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ExpenseForm
              buckets={[{ id: paying.bucket_id, name: paying.bucket_name }]}
              accounts={accounts.data}
              payout={{
                invoiceId: paying.invoice_id,
                invoiceNo: paying.invoice_no,
                currency: paying.currency,
              }}
              initialBucketId={paying.bucket_id}
              initialPayee={paying.payee}
              initialAmount={payPrefill(paying).amount}
              initialFxRate={payPrefill(paying).rate}
              submitLabel="Record payout"
              onSubmit={async (input) => {
                await record.mutateAsync(input);
                setPaying(null);
              }}
              onCancel={() => setPaying(null)}
            />
          </CardContent>
        </Card>
      )}

      {data.not_converted.length > 0 && (
        <Alert
          variant="warning"
          title={`Money received with no rupee figure yet: ${formatTotals(data.not_converted)}`}
        >
          Its shares are due but not valued in rupees, and the funds leave it out. Add the bank's
          eFIRA figure or the rate on the payment.
        </Alert>
      )}

      <section aria-labelledby="payouts-owed-heading" className="flex flex-col gap-2">
        <h3 id="payouts-owed-heading" className="text-sm font-semibold text-muted-foreground">
          Owed now, per payee
        </h3>
        {owed.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Nothing is due: every share of the money received is paid out.
          </p>
        ) : (
          <div className="table-frame">
            <table className="data-table">
              <thead className={THEAD}>
                <tr>
                  <th className={TH}>Bucket</th>
                  <th className={TH}>Payee</th>
                  <th className={`${TH} text-right`}>Due</th>
                  <th className={`${TH} text-right`}>In rupees</th>
                  <th className={`${TH} text-right`}>Paid out so far</th>
                </tr>
              </thead>
              <tbody>
                {owed.map((total) => (
                  <tr key={`${total.bucket_id}-${total.payee}`} className="border-b last:border-0">
                    <td className="px-4 py-2">{total.bucket_name}</td>
                    <td className="px-4 py-2">{payeeOf(total)}</td>
                    <td className={`${TD} font-medium`}>{formatTotals(total.due)}</td>
                    <td className={TD}>{rupeesOwed(total)}</td>
                    <td className={TD}>{formatTotals(total.paid_out)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="text-xs text-muted-foreground">
          Due is each bucket's share of what clients have paid, less what was paid out against
          that invoice. Rupees are at the rate each invoice's money actually came to.
        </p>
      </section>

      <section aria-labelledby="payouts-rows-heading" className="flex flex-col gap-3">
        <h3 id="payouts-rows-heading" className="text-sm font-semibold text-muted-foreground">
          Each invoice's shares
        </h3>
        <div className="grid gap-3 sm:grid-cols-[minmax(0,16rem)_1fr] sm:items-end sm:gap-4">
          <SelectField
            id="payouts-bucket"
            label="Bucket"
            value={bucketId}
            onChange={setBucketId}
            placeholder="All buckets"
            options={bucketOptions}
          />
          <Checkbox
            id="payouts-settled"
            label="Show settled shares and those waiting for the client"
            checked={includeSettled}
            onChange={setIncludeSettled}
            className="sm:pb-2"
          />
        </div>
        <RequestError error={accounts.error} />
        {rows.length === 0 ? (
          <EmptyState
            title="Nothing to pay out"
            description="Every share of the money received so far is paid out."
          />
        ) : (
          <div className="table-frame">
            <table className="data-table">
              <thead className={THEAD}>
                <tr>
                  <th className={TH}>Invoice</th>
                  <th className={TH}>Bucket</th>
                  <th className={`${TH} text-right`}>Share</th>
                  <th className={`${TH} text-right`}>Came in</th>
                  <th className={`${TH} text-right`}>Paid out</th>
                  <th className={`${TH} text-right`}>Pending</th>
                  <th className={TH}>Status</th>
                  <th className={TH}>
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <PayoutRow
                    key={`${row.invoice_id}-${row.bucket_id}`}
                    row={row}
                    onPay={paying ? undefined : () => setPaying(row)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section aria-labelledby="payouts-funds-heading" className="flex flex-col gap-2">
        <h3 id="payouts-funds-heading" className="text-sm font-semibold text-muted-foreground">
          What each bucket holds (INR)
        </h3>
        <div className="table-frame">
          <table className="data-table">
            <thead className={THEAD}>
              <tr>
                <th className={TH}>Bucket</th>
                <th className={`${TH} text-right`}>Its share of what came in</th>
                <th className={`${TH} text-right`}>Spent or paid out</th>
                <th className={`${TH} text-right`}>Fund</th>
              </tr>
            </thead>
            <tbody>
              {data.funds.map((fund) => (
                <tr key={fund.bucket_id} className="border-b last:border-0">
                  <td className="px-4 py-2">{fund.name}</td>
                  <td className={TD}>{formatInr(fund.received_inr)}</td>
                  <td className={TD}>{formatInr(fund.spent_inr)}</td>
                  <td className={`${TD} ${Number(fund.fund_inr) < 0 ? "text-destructive" : ""}`}>
                    {formatInr(fund.fund_inr)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-muted-foreground">
          For a pool such as the Reserve Fund the fund is the money set aside; for IT it is what is
          left after the bills charged to it. A negative fund means more went out than came in.
        </p>
      </section>
    </div>
  );
}

function PayoutRow({ row, onPay }: { row: PayoutDueRow; onPay?: () => void }) {
  const money = (amount: string) => formatMoney(amount, row.currency);
  const status = STATUS[row.status];
  const pending = Number(row.pending);
  return (
    <tr className="border-b last:border-0">
      <td className="whitespace-nowrap px-4 py-2">
        <Link to={`/invoices/${row.invoice_id}`} className="text-primary hover:underline">
          {row.invoice_no ?? "Draft"}
        </Link>
        <span className="block text-xs text-muted-foreground">{row.client_name}</span>
      </td>
      <td className="px-4 py-2">
        <span className="font-medium">{row.bucket_name}</span>
        {row.payee && <span className="block text-xs text-muted-foreground">{row.payee}</span>}
      </td>
      <td className={TD}>
        {Number(row.percent)}%
        <span className="block text-xs text-muted-foreground">of {money(row.planned)}</span>
      </td>
      <td className={TD}>{money(row.released)}</td>
      <td className={TD}>{Number(row.paid_out) > 0 ? money(row.paid_out) : "—"}</td>
      <td className={`${TD} ${pending > 0 ? "font-medium" : ""}`}>
        {money(row.pending)}
        {pending !== 0 && (
          <span className="block text-xs text-muted-foreground">
            {row.pending_inr === null ? "not valued yet" : `≈ ${formatInr(row.pending_inr)}`}
          </span>
        )}
      </td>
      <td className="px-4 py-2">
        <Badge variant={status.tone}>{status.label}</Badge>
      </td>
      <td className="px-4 py-2 text-right">
        {pending > 0 && onPay && (
          <Button size="sm" variant="outline" onClick={onPay}>
            Pay
          </Button>
        )}
      </td>
    </tr>
  );
}
